import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {Input} from '../../Input/Input'
import {Button} from '../../Button/Button'
import {POST, PUT, GET} from '../../../services/api'
import type {TypeSessionOption} from '../../../pages/Hospitality/Content/Content/types'
import {typeSessionOptions as sharedTypeSessionOptions} from '../../../pages/Hospitality/Content/Content/types'
import uuid from 'react-uuid'
import Swal from 'sweetalert2'
import {EditorQuill} from '../../EditorQuill/EditorQuill'
import {i18n} from '../../../translate/i18n'

type Props = {
  show: boolean
  onClose: () => void
  onCreated: () => void
  typeSessionOptions?: TypeSessionOption[]
  categories?: any[]
  formServices?: any[]
  hotelPostPages?: any[]
  serviceReservations?: any[]
}

enum typeContentEnum {
  PAGE = 'page_dinamic',
  GENERAL = 'general',
  SERVICE = 'service_form',
  SERVICE_RESERVATION = 'service_reservation',
}

const CreateContentModal: FC<Props> = ({
  show,
  onClose,
  onCreated,
  typeSessionOptions,
  categories,
  formServices,
  hotelPostPages,
  serviceReservations,
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedClassObj, setSelectedClassObj] = useState<any | null>(null)
  const [selectedServiceObj, setSelectedServiceObj] = useState<any | null>(null)
  const [selectedParent, setSelectedParent] = useState<number | null>(null)
  const [selectedChild, setSelectedChild] = useState<number | string | null>(null)
  const [selectedRoomServiceForForm, setSelectedRoomServiceForForm] = useState<number | null>(null)
  const [selectedParentName, setSelectedParentName] = useState<string | null>(null)
  const [selectedChildName, setSelectedChildName] = useState<string | null>(null)
  const [order, setOrder] = useState<string>('0')

  const [type, setType] = useState<typeContentEnum>(typeContentEnum.GENERAL)

  const resolvedTypeSessionOptions: TypeSessionOption[] =
    typeSessionOptions ?? sharedTypeSessionOptions

  const allOptions = [
    {
      select: `${i18n.t('hospitality_content.create_modal.placeholder_select_content')}`,
      value: 0,
      type: typeContentEnum.GENERAL,
    },
    // backend services
    ...(formServices ?? []).map((s: any) => ({
      select: `${s.title ?? s.name} - ${i18n.t('hospitality_content.messages.form')}`,
      value: s.id,
      type: typeContentEnum.SERVICE,
    })),
    // hotel post pages
    ...(hotelPostPages ?? []).map((p: any) => ({
      select: `${p.title} - ${i18n.t('hospitality_content.messages.page')}`,
      value: p.id,
      type: typeContentEnum.PAGE,
    })),
    // service reservations
    ...(serviceReservations ?? []).map((r: any) => ({
      select: `${r.title} - ${i18n.t('hospitality_content.messages.service_reservation')}`,
      value: r.id,
      type: typeContentEnum.SERVICE_RESERVATION,
    })),

    // static session types
    ...resolvedTypeSessionOptions.map((t: TypeSessionOption) => ({
      ...t,
      type: typeContentEnum.GENERAL,
    })),
  ]

  const handleClassChange = (value: any) => {
    setSelectedClassObj(value)
    setSelectedParent(value?.value || null)
    setSelectedParentName(value?.select || value?.label || null)
  }

  const handleServiceChange = (value: {label: string; value: number | string}) => {
    const find = allOptions.find((opt: any) => opt.select === value?.label)

    setSelectedServiceObj(value)
    setSelectedChild(value?.value || null)
    setSelectedChildName(value?.label || null)
    find && setType(find.type)
  }

  const create = async () => {
    try {
      setLoading(true)

      if (!selectedParent) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.create_modal.swal.missing_field_title')),
          text: String(i18n.t('hospitality_content.create_modal.swal.missing_section')),
        })
        setLoading(false)
        return
      }

      if (!selectedChild) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.create_modal.swal.missing_field_title')),
          text: String(i18n.t('hospitality_content.create_modal.swal.missing_content')),
        })
        setLoading(false)
        return
      }

      if (Number(order) < 0) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_content.create_modal.swal.invalid_field_title')),
          text: String(i18n.t('hospitality_content.create_modal.swal.invalid_order')),
        })
        setLoading(false)
        return
      }

      const payload: any = {
        sessionHotelClassificationId: selectedParent,
        status: true,
        order: Number(order) || 0,
      }

      switch (type) {
        case typeContentEnum.PAGE:
          payload.typeSession = 'page_dynamic'
          payload.hotelPostPagesId = selectedChild
          break
        case typeContentEnum.GENERAL:
          payload.typeSession = selectedChild
          break
        case typeContentEnum.SERVICE:
          payload.typeSession = 'service_form'
          payload.roomServiceId = selectedChild
          break
        case typeContentEnum.SERVICE_RESERVATION:
          payload.typeSession = 'service_reservation'
          payload.postId = selectedChild
          break
      }

      const res = await POST('/session-hotel', payload)

      setLoading(false)
      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_content.create_modal.swal.created_title')),
          text: String(i18n.t('hospitality_content.create_modal.swal.created_text')),
        }).then(() => {
          onCreated()
          onClose()
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_content.messages.filter_error_title')),
          text: res.error ?? String(i18n.t('hospitality_content.create_modal.swal.create_error')),
        })
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_content.messages.filter_error_title')),
        text: e ?? String(i18n.t('hospitality_content.create_modal.swal.create_error')),
      })
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{i18n.t('hospitality_content.create_modal.modal_title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='row'>
          <div className='col-12 mb-4'>
            <div className='alert alert-info'>
              <div className='d-flex'>
                <i className='fas fa-info-circle me-3 mt-1'></i>
                <div>
                  <strong>{i18n.t('hospitality_content.create_modal.alert_title')}</strong>
                  <br />
                  {i18n.t('hospitality_content.create_modal.alert_subtitle')}
                </div>
              </div>
            </div>
          </div>

          <div className='col-md-6 mb-4'>
            <label className='form-label fw-bold'>
              <i className='fas fa-layer-group me-3 mt-1'></i>
              {i18n.t('hospitality_content.create_modal.label_section')}
            </label>
            <Input
              inputAttr={{type: 'select'}}
              options={[
                {
                  select: `${i18n.t(
                    'hospitality_content.create_modal.placeholder_select_section'
                  )}`,
                  value: 0,
                },
                ...(categories ?? []).map((c: any) => ({select: c.name, value: c.id})),
              ]}
              value={selectedClassObj}
              placeholder={String(
                i18n.t('hospitality_content.create_modal.placeholder_select_section')
              )}
              search={true}
              change={handleClassChange}
            />
          </div>

          <div className='col-md-6 mb-4'>
            <label className='form-label fw-bold'>
              <i className='fas fa-th-large me-3 mt-1'></i>
              {i18n.t('hospitality_content.create_modal.label_content')}
            </label>
            <Input
              inputAttr={{type: 'select'}}
              options={allOptions.filter((opt: any) => opt.value !== selectedClassObj?.value)}
              value={selectedServiceObj}
              placeholder={String(
                i18n.t('hospitality_content.create_modal.placeholder_select_content')
              )}
              search={true}
              change={handleServiceChange}
            />
          </div>

          <div className='col-md-12 mb-4'>
            <Input
              inputAttr={{type: 'number'}}
              label={String(i18n.t('hospitality_content.create_modal.order_label'))}
              value={order}
              change={(v: string) => setOrder(v)}
            />
          </div>

          {selectedParentName && selectedChildName && (
            <div className='col-12 mt-6'>
              <div className='alert alert-success'>
                <div className='d-flex'>
                  <i className='fas fa-check-circle me-3 mt-1'></i>
                  <div>
                    <strong>{i18n.t('hospitality_content.create_modal.selection_summary')}</strong>
                    <br />
                    <div className='mt-2'>
                      <span className='badge badge-primary me-2'>
                        {i18n.t('hospitality_content.create_modal.badge_section')}
                      </span>
                      <strong>{selectedParentName}</strong>
                    </div>
                    <div className='mt-1'>
                      <span className='badge badge-primary me-2'>
                        {i18n.t('hospitality_content.create_modal.badge_content')}
                      </span>
                      <strong>{selectedChildName}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* If the selected type is service_form we need user to pick a numeric roomServiceId */}
          {selectedChild === 'service_form' && (
            <div className='col-md-12 mb-4'>
              <label className='form-label fw-bold'>
                <i className='fas fa-list me-2'></i>
                {i18n.t('hospitality_content.create_modal.select_form_label')}
              </label>
              <Input
                inputAttr={{type: 'select'}}
                options={[
                  {
                    select: `${i18n.t(
                      'hospitality_content.create_modal.placeholder_select_content'
                    )}`,
                    value: 0,
                  },
                  ...(formServices ?? []).map((s: any) => ({
                    select: s.title ?? s.name,
                    value: s.id,
                  })),
                ]}
                value={selectedRoomServiceForForm}
                placeholder={String(
                  i18n.t('hospitality_content.create_modal.placeholder_select_content')
                )}
                search={true}
                change={(v: any) => setSelectedRoomServiceForForm(v?.value ?? v)}
              />
            </div>
          )}

          <div className='col-12'>
            <div className='card'>
              <div className='card-body p-3'>
                <h6 className='card-title mb-2'>
                  <i className='fas fa-lightbulb me-2'></i>
                  {i18n.t('hospitality_content.create_modal.tips_title')}
                </h6>
                <ul className='mb-0 small text-muted'>
                  <li>{i18n.t('hospitality_content.create_modal.tips.item1')}</li>
                  <li>{i18n.t('hospitality_content.create_modal.tips.item2')}</li>
                  <li>{i18n.t('hospitality_content.create_modal.tips.item3')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          text={String(i18n.t('hospitality_content.create_modal.buttons.cancel'))}
          color='secondary'
          size='small'
          click={onClose}
        />
        <Button
          text={String(i18n.t('hospitality_content.create_modal.buttons.create'))}
          color='primary'
          size='small'
          click={create}
          loading={loading}
          disabled={
            !selectedClassObj ||
            !selectedServiceObj ||
            (selectedChild === 'service_form' && !selectedRoomServiceForForm)
          }
        />
      </Modal.Footer>

      {/* parent/child selection is inline now */}
    </Modal>
  )
}

export {CreateContentModal}
